/**
 * AGT-LST-015 — tokenized seller performance report (client-safe, unauthenticated).
 *
 * The body is composed from `payload.composition.sections` (migration 812), in the
 * order the resolved composition gives, rather than a hard-coded stack. The agency
 * template decides which sections exist and the listing agent can hide any of them
 * except the agent's contact details; both decisions are resolved on the backend,
 * so this page only renders what it is handed.
 *
 * Section headings are deliberately NOT the registry labels: those are written for
 * the agent configuring the template ("Headline numbers"), not for the vendor.
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AlertTriangle, BarChart3, Loader2, MapPin } from 'lucide-react'
import { api, type SellerReportPayload, type SellerReportStatus } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Numeric } from '@/components/ui/numeric'
import { brandFontFamily, brandPrimary } from '@/lib/agencyBrand'
import { usePageTitle } from '@/lib/usePageTitle'

type ViewState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; status: SellerReportStatus; payload: SellerReportPayload }

/**
 * Used when the payload carries no composition — a report frozen before migration
 * 812 returns its snapshot verbatim, and that snapshot has no composition in it.
 * These five are the backend registry's defaults, so such a report renders exactly
 * as it did when it was frozen.
 */
const FALLBACK_SECTION_ORDER = ['summary', 'agent_update', 'marketing', 'snapshot', 'agent_contact']

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount)
  } catch {
    return `${currency} ${amount.toLocaleString()}`
  }
}

export function SellerReportSharePage() {
  const { shareToken } = useParams<{ shareToken: string }>()
  const [state, setState] = useState<ViewState>({ kind: 'loading' })
  usePageTitle('Property performance report')

  useEffect(() => {
    if (!shareToken) {
      setState({ kind: 'error', message: 'This report link is invalid.' })
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const response = await api.getPublicSellerReport(shareToken)
        if (cancelled) return
        setState({ kind: 'ready', status: response.status, payload: response.payload })
      } catch {
        if (cancelled) return
        setState({ kind: 'error', message: 'This report is no longer available.' })
      }
    })()
    return () => { cancelled = true }
  }, [shareToken])

  if (state.kind === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--lc-surface-base)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (state.kind === 'error') {
    return (
      <div className="mx-auto flex min-h-screen max-w-lg flex-col items-center justify-center px-4 text-center">
        <AlertTriangle className="mb-3 h-8 w-8 text-[var(--lc-status-unpublished-fg)]" />
        <h1 className="text-xl font-semibold">Report unavailable</h1>
        <p className="mt-2 text-sm text-muted-foreground">{state.message}</p>
      </div>
    )
  }

  const { payload, status } = state
  const sectionIds = payload.composition?.sections?.length
    ? payload.composition.sections.map((section) => section.id)
    : FALLBACK_SECTION_ORDER
  // Step 3. `branding` is null for a personal-workspace listing and absent on a
  // snapshot frozen before it existed; both render unbranded rather than failing.
  const branding = payload.branding ?? null
  const primary = brandPrimary(branding?.primary_color)

  return (
    <div
      className="min-h-screen bg-[var(--lc-surface-base)]"
      style={{ fontFamily: brandFontFamily(branding?.font_family) }}
    >
      {branding && (
        // The agency's identity, as an accent strip. The brand colour is a rule and
        // a logo backdrop, never the background behind token-coloured text — see
        // lib/agencyBrand.ts.
        <div
          className="border-b-2 bg-[var(--lc-surface-raised)] px-4 py-3 sm:px-6"
          style={{ borderBottomColor: primary }}
        >
          <div className="mx-auto flex max-w-3xl items-center gap-3">
            {branding.logo_url ? (
              <img
                src={branding.logo_url}
                alt={branding.name}
                className="h-8 w-auto max-w-[180px] object-contain"
              />
            ) : (
              <span className="text-sm font-semibold" style={{ color: primary }}>
                {branding.name}
              </span>
            )}
          </div>
        </div>
      )}
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        {status === 'frozen' && (
          <div className="mb-4 rounded-md border border-[var(--lc-border-subtle)] bg-[var(--lc-surface-raised)] px-4 py-3 text-sm">
            Campaign ended — this is the final snapshot shared by your agent.
          </div>
        )}

        {/* Page header, not a section: it identifies the report and is always shown. */}
        <div className="rounded-[var(--lc-radius-lg)] bg-[var(--lc-surface-inverse)] px-5 py-6 text-[var(--lc-text-on-inverse)]">
          <p className="text-xs uppercase tracking-wide text-[var(--lc-text-on-inverse-muted)]">Performance report</p>
          <h1 className="mt-1 text-2xl font-semibold">{payload.property.title}</h1>
          <p className="mt-2 flex items-center gap-1 text-sm text-[var(--lc-text-on-inverse-muted)]">
            <MapPin className="h-4 w-4" />
            {payload.property.location_label}
          </p>
          <p className="mt-4 text-sm sm:text-base">{payload.report.state_of_play}</p>
        </div>

        {sectionIds.map((sectionId) => (
          <ReportSection key={sectionId} sectionId={sectionId} payload={payload} />
        ))}
      </div>
    </div>
  )
}

function ReportSection({ sectionId, payload }: { sectionId: string; payload: SellerReportPayload }) {
  switch (sectionId) {
    case 'summary':
      return payload.is_empty ? (
        <Card className="mt-6">
          <CardContent className="p-6 text-sm text-muted-foreground">
            Your campaign just started. Your agent will share updated numbers here as marketing goes live.
          </CardContent>
        </Card>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <MetricCard label="Days on market" value={payload.summary.days_on_market} />
          <MetricCard label="Posts live" value={payload.summary.posts_count} />
          <MetricCard label="Inquiries received" value={payload.summary.inquiries_received} />
          <MetricCard label="Qualified leads" value={payload.summary.qualified_inquiries} />
        </div>
      )

    case 'agent_update':
      // Omitted rather than shown empty: a heading with nothing under it reads as
      // an agent who had nothing to say.
      if (!payload.report.agent_summary) return null
      return (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Agent update</CardTitle>
          </CardHeader>
          <CardContent className="whitespace-pre-wrap text-sm text-[var(--lc-text-primary)]">
            {payload.report.agent_summary}
          </CardContent>
        </Card>
      )

    case 'marketing':
      return (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="h-4 w-4" />
              Marketing activity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {payload.marketing.channels.length === 0 ? (
              <p className="text-sm text-muted-foreground">Your listing promotion is getting started.</p>
            ) : (
              payload.marketing.channels.map((channel) => (
                <div key={channel.id} className="flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm">
                  <div>
                    <p className="font-medium capitalize">{channel.platform}</p>
                    <p className="text-xs text-muted-foreground"><Numeric>{channel.views}</Numeric> views</p>
                  </div>
                  <Badge variant="published">Live</Badge>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )

    case 'performance': {
      const totals = payload.performance.all_channels
      return (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Where the interest is coming from</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <Stat label="Views" value={totals.impressions} />
              <Stat label="Engagements" value={totals.engagements} />
              <Stat label="Clicks" value={totals.clicks} />
            </div>
            {payload.performance.per_channel.length === 0 ? (
              <p className="text-sm text-muted-foreground">Channel figures appear once posts have been live for a day.</p>
            ) : (
              <div className="space-y-2">
                {payload.performance.per_channel.map((channel) => (
                  <div key={channel.platform} className="flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm">
                    <span className="font-medium capitalize">{channel.platform}</span>
                    <span className="text-muted-foreground">
                      <Numeric>{channel.impressions}</Numeric> views · <Numeric>{channel.inquiries}</Numeric> inquiries
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )
    }

    case 'feedback':
      return (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Viewing feedback</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {payload.feedback.length === 0 ? (
              <p className="text-sm text-muted-foreground">No viewing feedback to share yet.</p>
            ) : (
              payload.feedback.map((item) => (
                <div key={item.id} className="rounded-md border border-[var(--lc-border-subtle)] px-3 py-2 text-sm">
                  <p>{item.outcome}</p>
                  {item.scheduled_at && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {new Date(item.scheduled_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )

    case 'offers':
      return (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Offers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {payload.offers.length === 0 ? (
              <p className="text-sm text-muted-foreground">No offers have been submitted yet.</p>
            ) : (
              payload.offers.map((offer) => (
                <div key={offer.id} className="flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm">
                  <div>
                    <p className="font-medium capitalize">{offer.status.replace(/_/g, ' ')}</p>
                    {offer.offer_date && (
                      <p className="text-xs text-muted-foreground">{new Date(offer.offer_date).toLocaleDateString()}</p>
                    )}
                  </div>
                  {/* The backend withholds the figure unless the agent enabled it;
                      amount_hidden is the signal, so never fall back to 0. */}
                  <span className="font-medium">
                    {offer.amount_hidden || offer.amount === undefined
                      ? 'Amount shared privately'
                      : formatMoney(offer.amount, offer.currency || payload.property.price_unit)}
                  </span>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )

    case 'scores': {
      const entries = ([
        ['Overall', payload.scores.overall],
        ['Quality', payload.scores.quality],
        ['Location', payload.scores.location],
        ['Condition', payload.scores.cleanliness],
      ] as const).filter(([, value]) => value !== null)
      return (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Property scores</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {entries.length === 0 ? (
              <p className="text-muted-foreground">Scores have not been assessed yet.</p>
            ) : (
              entries.map(([label, value]) => (
                <p key={label}>
                  <span className="text-muted-foreground">{label}:</span> <Numeric>{value as number}</Numeric>
                </p>
              ))
            )}
          </CardContent>
        </Card>
      )
    }

    case 'snapshot':
      return (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Listing snapshot</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><span className="text-muted-foreground">Ask:</span> {formatMoney(payload.property.price, payload.property.price_unit)}</p>
            <p><span className="text-muted-foreground">Inquiries shielded:</span> <Numeric>{payload.summary.shielded_inquiries}</Numeric></p>
            <p><span className="text-muted-foreground">Offers:</span> <Numeric>{payload.summary.offers_count}</Numeric></p>
          </CardContent>
        </Card>
      )

    case 'agent_contact':
      if (!payload.agent) return null
      return (
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Prepared by {payload.agent.name}
          {payload.agent.agency_name ? ` · ${payload.agent.agency_name}` : ''}
        </p>
      )

    default:
      // A section the backend knows about and this build does not. Rendering
      // nothing is correct: the alternative is an empty titled card the vendor
      // cannot make sense of.
      return null
  }
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-[var(--lc-border-subtle)] px-3 py-2">
      <p className="text-xs uppercase tracking-wide text-[var(--lc-text-muted)]">{label}</p>
      <p className="mt-1 font-semibold"><Numeric>{value}</Numeric></p>
    </div>
  )
}

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-[var(--lc-radius-lg)] border border-[var(--lc-border-subtle)] bg-[var(--lc-surface-raised)] p-4 shadow-[var(--lc-elevation-sm)]">
      <p className="text-[length:var(--lc-type-overline)] uppercase tracking-wide text-[var(--lc-text-muted)]">{label}</p>
      <p className="mt-2 text-[length:var(--lc-type-display)] font-semibold">
        <Numeric>{value}</Numeric>
      </p>
    </div>
  )
}
